import React, { useState, useEffect } from 'react';
import axios from 'axios';
// 1. IMPORTACIONES DE MICROSOFT
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser"; // <--- NUEVO IMPORT NECESARIO
import { loginRequest } from "./authConfig";

const API_BASE_URL = "https://as400.atlanticu.edu";

// --- 🔒 LISTA BLANCA ---
const ALLOWED_EMAILS = [
  "rgarcia@atlanticu.edu", // <--- ¡Verifica tu correo aquí!
  "afernandez@atlanticu.edu",
  "egutierrez@atlanticu.edu",
  "anegron@atlanticu.edu",
  "ycolon@atlanticu.edu"
];

function App() {
  // 2. OBTENER ESTADO DE PROGRESO (inProgress)
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isAuthorized, setIsAuthorized] = useState(false);

  // Estados de la App
  const [searchId, setSearchId] = useState('');
  const [student, setStudent] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // --- LÓGICA DE SEGURIDAD ---
  useEffect(() => {
    if (isAuthenticated && accounts[0]) {
      const userEmail = accounts[0].username.toLowerCase();
      console.log("✅ Usuario autenticado:", userEmail);

      if (ALLOWED_EMAILS.includes(userEmail)) {
        setIsAuthorized(true);
      } else {
        setIsAuthorized(false);
      }
    }
  }, [isAuthenticated, accounts]);

  const handleLogin = () => {
    // Usamos redirect para evitar bloqueos
    instance.loginRedirect(loginRequest).catch(e => console.error(e));
  };

  const handleLogout = () => instance.logoutRedirect().catch(e => console.error(e));

  // --- LÓGICA DE NEGOCIO (Búsqueda, Impresión, etc.) ---
  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setStudent(null);
    setSearchResults([]);

    try {
      const response = await axios.get(`${API_BASE_URL}/api/estudiantes/buscar?q=${searchId}`);
      if (response.data.length === 0) {
        setError('❌ No se encontraron estudiantes.');
      } else if (response.data.length === 1) {
        selectStudent(response.data[0].student_id);
      } else {
        setSearchResults(response.data);
      }
    } catch (err) {
      console.error(err);
      setError('❌ Error de conexión.');
    } finally {
      setLoading(false);
    }
  };

  const selectStudent = async (id) => {
    setLoading(true);
    setSearchResults([]);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/estudiantes/${id}`);
      setStudent(response.data);
    } catch (err) {
      setError('❌ Error al cargar detalles.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  const groupCoursesByTerm = (historial) => {
    if (!historial) return {};
    const grouped = historial.reduce((acc, curso) => {
      const termClean = curso.term_id.replace('.0', '');
      if (!acc[termClean]) acc[termClean] = [];
      acc[termClean].push(curso);
      return acc;
    }, {});
    return Object.keys(grouped).sort().reduce((obj, key) => {
      obj[key] = grouped[key];
      return obj;
    }, {});
  };

  // ---------------------------------------------------------
  // 🔄 PANTALLA DE CARGA (MIENTRAS MICROSOFT PROCESA)
  // ---------------------------------------------------------
  if (inProgress !== InteractionStatus.None) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-10 text-white">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#662d91] mb-4"></div>
        <p className="text-xl font-semibold">Validando credenciales...</p>
        <p className="text-sm text-slate-400">Por favor espere un momento.</p>
      </div>
    );
  }

  // --- PANTALLA 1: NO AUTENTICADO ---
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
        <div className="bg-white p-10 rounded-2xl shadow-2xl text-center max-w-md w-full">
          <img src="/AtlanticLogo_Vert_Color.png" alt="Logo" className="h-20 mx-auto mb-6 object-contain" />
          <h1 className="text-3xl font-extrabold text-[#662d91] mb-2">Atlantic University</h1>
          <p className="text-slate-500 mb-8">Sistema de Gestión Académica</p>
          <button
            onClick={handleLogin}
            className="w-full bg-[#662d91] hover:bg-[#3b0256] text-white py-3 rounded-xl font-bold transition-all shadow-md hover:scale-105"
          >
            Ingresar con Microsoft 365
          </button>
        </div>
      </div>
    );
  }

  // --- PANTALLA 2: NO AUTORIZADO (Lista Blanca) ---
  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-red-50 flex items-center justify-center p-10">
        <div className="text-center bg-white p-10 rounded-xl shadow-lg border border-red-100">
          <div className="text-5xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-red-600 mb-2">Acceso Restringido</h2>
          <p className="text-slate-600 mb-6">
            La cuenta <strong>{accounts[0]?.username}</strong> no tiene permisos.
          </p>
          <button onClick={handleLogout} className="bg-slate-200 hover:bg-slate-300 px-6 py-2 rounded-lg font-bold">Cerrar Sesión</button>
        </div>
      </div>
    );
  }

  // --- PANTALLA 3: SISTEMA (Si pasó todo) ---
  return (
    <div className="min-h-screen bg-slate-50 p-6 md:p-10 font-sans text-slate-800">
      <div className="max-w-5xl mx-auto">
        {/* HEADER */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 border-b pb-6 print:hidden">
          <div className="flex items-center gap-6">
            <img src="/AtlanticLogo_Vert_Color.png" alt="Atlantic Logo" className="h-16 w-auto object-contain" />
            <div>
              <h1 className="text-3xl font-extrabold text-[#662d91]">Atlantic University</h1>
              <p className="text-[#3b0256] text-xl font-bold mt-1">Plataforma de Gestión Académica</p>
            </div>
          </div>
          <div className="mt-4 md:mt-0 flex flex-col items-end gap-2">
            <div className="text-right">
              <p className="text-sm font-bold text-slate-700">{accounts[0]?.name}</p>
              <button onClick={handleLogout} className="text-xs text-red-500 hover:underline">Cerrar Sesión</button>
            </div>
            {student && (
              <button onClick={handlePrint} className="bg-slate-800 hover:bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2">
                🖨️ Imprimir PDF
              </button>
            )}
          </div>
        </div>

        {/* HEADER IMPRESIÓN */}
        <div className="hidden print:block mb-8 text-center border-b pb-4">
          <img src="/AtlanticLogo_Vert_Color.png" alt="Atlantic Logo" className="h-12 mx-auto mb-2" />
          <h1 className="text-2xl font-bold text-black">Atlantic University College</h1>
          <p className="text-sm text-gray-600">Historial Académico Oficial</p>
        </div>

        {/* BUSCADOR */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 mb-8 print:hidden">
          <form onSubmit={handleSearch} className="flex gap-3">
            <input
              type="text"
              autoFocus
              placeholder="Buscar por ID, Nombre o Apellidos..."
              className="flex-1 p-3 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-[#662d91]"
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
            />
            <button type="submit" className="bg-[#662d91] hover:bg-[#3b0256] text-white px-6 py-3 rounded-lg font-bold" disabled={loading}>
              {loading ? '...' : 'Buscar'}
            </button>
          </form>
          {error && <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">⚠️ {error}</div>}
        </div>

        {/* RESULTADOS LISTA */}
        {searchResults.length > 0 && (
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 mb-8 animate-fade-in print:hidden">
            <h3 className="text-lg font-bold text-slate-700 mb-4">📍 {searchResults.length} coincidencias:</h3>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {searchResults.map((res) => (
                <div key={res.student_id} className="flex justify-between items-center p-3 hover:bg-slate-50 border rounded-lg cursor-pointer" onClick={() => selectStudent(res.student_id)}>
                  <div>
                    <p className="font-bold text-[#662d91]">{res.first_name} {res.last_name}</p>
                    <p className="text-sm text-slate-500">ID: {res.student_id} • {res.program_code}</p>
                  </div>
                  <span className="text-slate-400 text-sm">Ver ›</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* DETALLE */}
        {student && (
          <div className="animate-fade-in">
            {/* ... Tu código de detalle igual que antes ... */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mb-8 print:shadow-none print:border-0">
              <div className="bg-gradient-to-r from-[#662d91] to-[#3b0256] p-6 text-white print:bg-none print:text-black print:p-0 print:mb-4 print:border-b print:border-black">
                <h2 className="text-3xl font-bold">{student.perfil.first_name} {student.perfil.last_name}</h2>
                <p className="opacity-80 mt-1 print:text-slate-600">Expediente Académico</p>
              </div>
              <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-6 print:p-0 print:gap-2">
                <div><p className="text-xs text-slate-400 font-bold uppercase">ID</p><p className="text-lg font-semibold">{student.perfil.student_id}</p></div>
                <div><p className="text-xs text-slate-400 font-bold uppercase">Programa</p><p className="text-lg font-semibold">{student.perfil.program_code}</p></div>
                <div><p className="text-xs text-slate-400 font-bold uppercase">Género</p><p className="text-lg font-semibold">{student.perfil.gender}</p></div>
                <div><p className="text-xs text-slate-400 font-bold uppercase">Cursos</p><p className="text-lg font-semibold text-[#662d91]">{student.historial.length}</p></div>
              </div>
            </div>

            <div className="space-y-6">
              <h3 className="text-xl font-bold text-slate-700 border-b pb-2 print:text-black">Historial de Cursos</h3>
              {Object.keys(groupCoursesByTerm(student.historial)).length > 0 ? (
                Object.entries(groupCoursesByTerm(student.historial)).map(([trimestre, cursos]) => (
                  <div key={trimestre} className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden print:shadow-none print:border-0 print:mb-4">
                    <div className="bg-slate-50 px-6 py-2 border-b border-slate-200 flex justify-between items-center print:bg-transparent print:px-0 print:border-b-2 print:border-slate-800">
                      <span className="font-bold text-slate-700 print:text-black">🗓️ {trimestre}</span>
                      <span className="text-xs bg-white border px-2 py-1 rounded text-slate-500 print:hidden">{cursos.length} materias</span>
                    </div>
                    <table className="w-full text-left text-sm">
                      <thead>
                        <tr className="text-xs text-slate-400 border-b print:text-black">
                          <th className="px-6 py-2 font-medium w-24 print:px-0">CÓDIGO</th>
                          <th className="px-6 py-2 font-medium print:px-0">CRÉDITOS</th>
                          <th className="px-6 py-2 font-medium text-right print:px-0">NOTA</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50 print:divide-slate-200">
                        {cursos.map((curso, idx) => (
                          <tr key={idx} className="hover:bg-purple-50 print:hover:bg-transparent">
                            <td className="px-6 py-2 font-medium text-slate-700 print:px-0">{curso.course_code}</td>
                            <td className="px-6 py-2 text-slate-500 print:px-0">{curso.credits}</td>
                            <td className="px-6 py-2 text-right font-bold print:px-0">{curso.grade}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))
              ) : (
                <div className="p-10 text-center border border-dashed rounded-xl">Sin historial disponible.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;